import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTabs from '../../components/CustomTabs';
import Header from '../../components/Header';
import PrimaryBox from '../../components/PrimaryBox';
import PlanningAmplirollTable from '../../components/tables/operations/planning/PlanningAmplirollTable';
import PlanningGlobalTable from '../../components/tables/operations/planning/PlanningGlobalTable';
import PlanningMaterialPage from '../../components/tables/operations/planning/PlanningMaterialTable';
import ReportsPlanningTable from '../../components/tables/operations/planning/ReportsPlanningTable';
import { useActivitiesQuery } from '../../hooks/queries/useActivitiesQuery';
import { useActivitiesTypesQuery } from '../../hooks/queries/useActivitiesTypesQuery';
import { useTrucksQuery } from '../../hooks/queries/useTrucksQuery';
import { useUsersQuery } from '../../hooks/queries/useUsersQuery';
import { useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { DataItem } from '../../services/apiSLTP/activities';
import { capitalizeFirstLetter } from '../../utils/capitalize';
import { getTomorrowDate } from '../../utils/dates';
import { activityStateEnum, localStorageKeys, permissionLevels, stepModes } from '../../utils/enums';
import { getItemFromLocalStorage, storeItemToLocalStorage } from '../../utils/localStorage';

const PlanningPage: React.FC = () => {
    const [currentDate, setCurrentDate] = useState(new Date(getItemFromLocalStorage(localStorageKeys.DATE) || getTomorrowDate()));

    const [selectedTab, setSelectedTab] = useState(parseInt(getItemFromLocalStorage(localStorageKeys.TAB.PLANNING) || '0'));

    const [alignment, setAlignment] = React.useState<number | null>(0);

    const handleAlignment = (event: React.MouseEvent<HTMLElement>, newAlignment: number | null) => {
        if (newAlignment !== null) {
            setAlignment(newAlignment);
        }
    };

    const { user } = useAuth();
    const { t } = useTranslation();
    usePageTitle(capitalizeFirstLetter(t('planning')));

    const topTableRef = useRef<HTMLDivElement>(null);

    const {
        data: activities,
        isLoading,
        isError,
        isSuccess,
        isRefetching,
        refetch,
    } = useActivitiesQuery({
        queries: {
            startDate: currentDate.toISOString(),
            endDate: currentDate.toISOString(),
            stepMode: stepModes.PLANNING,
        },
    });
    const { data: dataTrucks, isLoading: trucksIsLoading, isError: trucksIsError, isSuccess: trucksIsSuccess } = useTrucksQuery({});
    const { data: dataUsers, isLoading: isUsersLoading } = useUsersQuery({ queries: { active: true } });
    const { data: dataActivitiesTypes, isLoading: isActivitiesTypesLoading } = useActivitiesTypesQuery({ queries: { active: true } });

    const handleDateChange = (newDate: string) => {
        setCurrentDate(new Date(newDate));
        storeItemToLocalStorage(localStorageKeys.DATE, newDate);
    };

    const handleTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedTab(newValue);
        storeItemToLocalStorage(localStorageKeys.TAB.PLANNING, newValue.toString());
    };

    const checkEditableRow = (activity: DataItem) => {
        if (user && user?.profileId.permsLevel > permissionLevels.PLANNING) return false;
        if (activity.activityState <= activityStateEnum.PLANNINGSENT) return false;
        return true;
    };

    return (
        <PrimaryBox>
            <Header
                title={t('operations')}
                breadcrumbs={[{ label: capitalizeFirstLetter(t('planning')) }]}
                subTitle={capitalizeFirstLetter(t('planning'))}
                initialDate={currentDate}
                onDateChange={(newDate) => handleDateChange(newDate.toISOString())}
            ></Header>

            <CustomTabs selectedTab={selectedTab} handleTabChange={handleTabChange} tabLabels={[t('overall'), t('reports'), t('material')]} />
            {selectedTab === 0 && (
                <>
                    <div style={{ display: alignment === 0 ? 'flex' : 'none', flexDirection: 'column', flexGrow: 1 }}>
                        <PlanningGlobalTable
                            activities={activities || []}
                            currentDate={currentDate}
                            refetch={refetch}
                            isLoading={isLoading}
                            checkEditableRow={checkEditableRow}
                            dataActivitiesTypes={dataActivitiesTypes || []}
                            dataUsers={dataUsers || []}
                            dataTrucks={dataTrucks || []}
                            isActivitiesTypesLoading={isActivitiesTypesLoading}
                            isUsersLoading={isUsersLoading}
                            handleAlignment={handleAlignment}
                        />
                    </div>
                    <div style={{ display: alignment === 1 ? 'flex' : 'none', flexDirection: 'column', flexGrow: 1 }}>
                        <PlanningAmplirollTable
                            activities={activities || []}
                            currentDate={currentDate}
                            refetch={refetch}
                            isLoading={isLoading}
                            checkEditableRow={checkEditableRow}
                            dataActivitiesTypes={dataActivitiesTypes || []}
                            dataUsers={dataUsers || []}
                            dataTrucks={dataTrucks || []}
                            isActivitiesTypesLoading={isActivitiesTypesLoading}
                            isUsersLoading={isUsersLoading}
                            handleAlignment={handleAlignment}
                        />
                    </div>
                </>
            )}

            {selectedTab === 1 && <ReportsPlanningTable currentDate={currentDate} refetch={refetch} activities={activities || []} />}
            {selectedTab === 2 && <PlanningMaterialPage currentDate={currentDate} />}
        </PrimaryBox>
    );
};

export default PlanningPage;
